import { forwardRef, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import SpellcheckContextMenuPopover from "@/components/SpellcheckContextMenuPopover";
import SpellcheckSuggestionBar from "@/components/SpellcheckSuggestionBar";
import { useSpellcheckContextMenu } from "@/hooks/useSpellcheckContextMenu";
import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";

/**
 * Campo con corrector: chips de sugerencia + menú al clic derecho.
 */
const SpellcheckInput = forwardRef(function SpellcheckInput({
  value,
  onChange,
  className,
  label,
  labelClassName,
  enableSpellcheck = true,
  spellCheck,
  lang = "es-MX",
  testId,
  wrapperClassName,
  type,
  minChars = 3,
  showSuggestionBar = true,
  ...props
}, ref) {
  const textLike = !type || type === "text" || type === "search";
  const embedded = isEmbeddedBrowserContext();

  const onApply = useCallback((next) => {
    onChange?.({ target: { value: next } });
  }, [onChange]);

  const {
    matches,
    menu,
    handleContextMenu,
    handleMouseUp,
    applySuggestion,
    applyMatchSuggestion,
    ignoreMatchSuggestion,
    useNativeSpellcheck,
  } = useSpellcheckContextMenu({
    value,
    onApply,
    enabled: enableSpellcheck && textLike,
    minChars,
  });

  const resolvedSpellCheck = spellCheck ?? (
    enableSpellcheck && textLike && useNativeSpellcheck ? true : false
  );

  return (
    <div className={cn("space-y-1.5", wrapperClassName)} data-testid={testId ? `${testId}-wrap` : undefined}>
      {label ? <Label className={cn("text-xs", labelClassName)}>{label}</Label> : null}
      <Input
        ref={ref}
        type={type}
        value={value}
        onChange={onChange}
        className={className}
        spellCheck={resolvedSpellCheck}
        lang={textLike && enableSpellcheck ? lang : undefined}
        autoCorrect="off"
        autoCapitalize="off"
        onContextMenu={handleContextMenu}
        onMouseUp={handleMouseUp}
        title={enableSpellcheck && textLike
          ? (embedded
            ? "Clic derecho o seleccione la palabra para ver sugerencias"
            : "Clic derecho sobre la palabra subrayada para ver sugerencias")
          : undefined}
        data-testid={testId}
        {...props}
      />
      {enableSpellcheck && textLike && showSuggestionBar ? (
        <SpellcheckSuggestionBar
          value={value}
          matches={matches}
          onApply={applyMatchSuggestion}
          onIgnore={ignoreMatchSuggestion}
          testId={testId ? `${testId}-suggestions` : "spellcheck-suggestion-bar"}
        />
      ) : null}
      <SpellcheckContextMenuPopover
        open={Boolean(menu)}
        x={menu?.x ?? 0}
        y={menu?.y ?? 0}
        suggestions={menu?.suggestions ?? []}
        onSelect={applySuggestion}
        testId={testId ? `${testId}-spell-menu` : undefined}
      />
    </div>
  );
});

export default SpellcheckInput;
